import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { convertToUsdString } from 'src/app/shared/utils/currency';

import { useRealtimeValidation } from '../../../../../../container/hooks/useRealtimeValidation';
import { useMembershipModalState } from '../../../../../../modalContext';

type Result = ModelHookResult<
	{
		amount: number;
		currency: string;
		currencySymbol: string;
		errorMessage: string | null;
		placeholder: string;
		minValue: number;
		maxValue: number;
		formatValue: (value: number) => string;
	},
	{
		onSliderChangeEnd: (value: number) => void;
		onInputChange: (value: string) => void;
		onInputFocus: () => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useSliderAmountSelector(): Result {
	const [
		{
			amount,
			currencyCode,
			validationErrors,
			maxAmount,
			minAmount,
			revenuePpmuMultiplePricesExpData,
			currencyExchangeRate,
		},
		{ setAmount },
	] = useMembershipModalState();
	const { getCurrencySymbol, translate } = useI18n();

	const [customAmount, setCustomAmount] = useState<number>(0);
	const [dynamicMaxValue, setDynamicMaxValue] = useState<number>(
		revenuePpmuMultiplePricesExpData.maxSliderAmount ?? maxAmount,
	);
	const track = useTracking();
	const toAmountUsd = useCallback(
		(amt: number) => convertToUsdString(amt, currencyExchangeRate),
		[currencyExchangeRate],
	);

	useRealtimeValidation();

	const maxValue = useMemo(() => Math.max(dynamicMaxValue, amount), [dynamicMaxValue, amount]);

	const currencySymbol = getCurrencySymbol(currencyCode);
	const formatValue = useCallback((value: number) => `${currencySymbol}${value}`, [currencySymbol]);

	useEffect(() => {
		void track('membership_ask_slider_selector_viewed', {
			local_amount: amount,
			amount: toAmountUsd(amount),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data: {
			amount,
			currency: currencyCode,
			currencySymbol,
			errorMessage: customAmount !== 0 ? (validationErrors?.amount ?? null) : null,
			placeholder: translate('corgi.co.promote.custom_amount'),
			maxValue,
			minValue: minAmount,
			formatValue,
		},
		actions: {
			onSliderChangeEnd: useCallback(
				(value: number) => {
					setAmount(value);
					setCustomAmount(value);
					void track('membership_ask_slider_amount_changed', {
						amount: toAmountUsd(value),
						local_amount: value,
					});
				},
				[setAmount, track, toAmountUsd],
			),
			onInputChange: useCallback(
				(value: string) => {
					if (/^\d*$/.test(value)) {
						const newAmount = Number(value);
						if (newAmount > 0) {
							setAmount(newAmount);
							setCustomAmount(newAmount);
							if (newAmount > dynamicMaxValue) setDynamicMaxValue(newAmount);
							void track('membership_ask_slider_amount_input_changed', {
								amount: toAmountUsd(newAmount),
								local_amount: newAmount,
							});
						} else if (value === '') {
							setAmount(0);
							setCustomAmount(0);
						}
					}
				},
				[setAmount, dynamicMaxValue, track, toAmountUsd],
			),
			onInputFocus: useCallback(() => {
				setCustomAmount(customAmount);
				void track('membership_ask_slider_amount_input_focused', {
					amount: toAmountUsd(customAmount),
					local_amount: customAmount,
				});
			}, [customAmount, track, toAmountUsd]),
		},
	};
}
